/**
 * CSV / TSV writer (Requirements 10.10, 10.11).
 *
 * ## Minimal quoting
 *
 * A field is quoted if and only if it contains the delimiter, the quote
 * character, a carriage return, or a line feed, or it begins or ends with
 * whitespace. Inside a quoted field the quote character is doubled. Nothing else
 * is quoted, so ordinary data stays readable — and the rule is exactly strong
 * enough to make the parser recover the original field, which is what
 * Requirement 10.11 asks for.
 *
 * Leading and trailing whitespace is in the rule because it is the one case
 * where an unquoted field survives a naive parser but loses meaning to readers
 * that trim: quoting pins it down.
 *
 * ## Every record is terminated
 *
 * The writer ends *every* record with the dialect newline, including the last.
 * Without a trailing terminator a final record of one empty field would be
 * indistinguishable from no record at all, and the round trip would lose it.
 * With one, the mapping from row sets to text is injective for every row set the
 * writer can express.
 *
 * A row with zero fields is not representable in CSV — it is written, and read
 * back, as a row with one empty field.
 */

import type { CsvDialect } from './dialect.js';
import { resolveDialect } from './dialect.js';

/** True when the minimal-quoting rule requires quotes around `field`. */
export function needsQuoting(field: string, dialect: CsvDialect): boolean {
  if (field.length === 0) return false;
  if (field.includes(dialect.delimiter)) return true;
  if (field.includes('"')) return true;
  if (field.includes('\r') || field.includes('\n')) return true;
  const first = field.charAt(0);
  const last = field.charAt(field.length - 1);
  return isWhitespace(first) || isWhitespace(last);
}

/** Whitespace test used by the quoting rule. */
function isWhitespace(ch: string): boolean {
  return /\s/u.test(ch);
}

/** Serialises one field, quoting only when the rule requires it. */
export function encodeCsvField(field: string, dialect: CsvDialect): string {
  if (!needsQuoting(field, dialect)) return field;
  return `"${field.replace(/"/g, '""')}"`;
}

/** Serialises one record, without its terminator. */
export function writeCsvRow(fields: readonly string[], dialect: CsvDialect): string {
  if (fields.length === 0) return '';
  let out = encodeCsvField(fields[0] as string, dialect);
  for (let i = 1; i < fields.length; i += 1) {
    out += dialect.delimiter + encodeCsvField(fields[i] as string, dialect);
  }
  return out;
}

/**
 * Serialises a row set into CSV / TSV text that the CSV_Parser accepts and reads
 * back unchanged (Requirements 10.10, 10.11, Property 37).
 */
export function writeCsv(
  rows: readonly (readonly string[])[],
  dialect: Partial<CsvDialect> = {},
): string {
  const d = resolveDialect(dialect);
  let out = '';
  for (const row of rows) out += writeCsvRow(row, d) + d.newline;
  return out;
}

/**
 * Streaming counterpart of {@link writeCsv}: yields one terminated record at a
 * time so the Export_Service (task 21) can pipe records into a writable stream
 * without materialising the whole file.
 */
export function* writeCsvRecords(
  rows: Iterable<readonly string[]>,
  dialect: Partial<CsvDialect> = {},
): Generator<string> {
  const d = resolveDialect(dialect);
  for (const row of rows) yield writeCsvRow(row, d) + d.newline;
}
